
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { AdminListingsTable } from "./AdminListingsTable";
import { AdminFilterPanel } from "./AdminFilterPanel";
import { useAdminListings } from "@/hooks/useAdminListings";
import { AdminListingFilters } from "@/lib/types/adminTypes";
import { toast } from "sonner";
import { Loader } from "lucide-react";

export function AdminListings() {
  const [filters, setFilters] = useState<AdminListingFilters>({
    page: 1,
    limit: 20,
    sort_by: "created_at",
    sort_order: "desc"
  });

  // Fetch listings with filters using the GET /api/admin/listings endpoint
  const { 
    data: listingsData, 
    isLoading: listingsLoading, 
    error: listingsError 
  } = useAdminListings(filters);

  // Handle API errors
  if (listingsError) {
    console.error("Listings error:", listingsError);
    toast.error("Failed to load listings. Please check your admin privileges.");
  }

  // Update filters handler
  const handleFilterChange = (newFilters: Partial<AdminListingFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 })); // Reset to page 1 when filters change
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      page: 1,
      limit: 20,
      sort_by: "created_at",
      sort_order: "desc"
    });
  };

  return (
    <div className="container py-8">
      <h1 className="text-3xl font-bold mb-4">All Property Listings</h1>
      <p className="text-gray-500 mb-8">
        Manage all property listings across the platform
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <AdminFilterPanel 
            filters={filters} 
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
          />
        </div>
        <div className="lg:col-span-3">
          {listingsData && (
            <AdminListingsTable 
              listings={listingsData?.listings || []} 
              isLoading={listingsLoading}
              pagination={listingsData?.pagination}
              onPageChange={handlePageChange}
              showPagination={true}
              limit={filters.limit || 20}
            />
          )}
          {!listingsData && listingsLoading && (
            <div className="flex justify-center py-8">
              <Loader className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}
          {!listingsData && !listingsLoading && (
            <Card className="p-8 text-center">
              <p className="text-muted-foreground">No listings found</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
